// ── AssistantShell ────────────────────────────────────────────────────────────
// Shared visual chrome for all contextual assistant panels.
// Handles: header + collapse strip, pinned pills grid (2-col cyan),
//          chip-above-input flow, standard footer (input + send).
// Parents supply: message content via children + send callback.

import { useState, useRef, useEffect, useCallback, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../Icon.js';
import { ClaudeAssistantPanel } from './ClaudeAssistantPanel.js';
import { useSpeechToText } from '../../hooks/useSpeechToText.js';
import { useSecretStatus } from '../../hooks/useSettings.js';

let voiceKeyframesInjected = false;
function injectVoiceKeyframes() {
  if (voiceKeyframesInjected || typeof document === 'undefined') return;
  voiceKeyframesInjected = true;
  const style = document.createElement('style');
  style.textContent = `
    @keyframes evo-mic-pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.45; }
    }
  `;
  document.head.appendChild(style);
}

// ── Types ─────────────────────────────────────────────────────────────────────

export interface AssistantPillDef {
  toolName: string;
  label: string;
  requiresInput: boolean;
}

interface Props {
  title: string;
  /** Pinned pills shown above message area. Click → chip-above-input flow for requiresInput. */
  pills?: AssistantPillDef[];
  onSendMessage: (message: string) => void;
  isStreaming?: boolean;
  inputPlaceholder?: string;
  /** Placeholder shown in footer input when a requiresInput pill is active. */
  chipPlaceholder?: string;
  inputDisabled?: boolean;
  /** localStorage key for collapse persistence — unique per module. Default: 'evo-assistant-collapsed'. */
  storageKey?: string;
  /** Custom footer (textarea panels, etc). Skips default input+send footer. */
  footerOverride?: ReactNode;
  /** Content pinned below header, above pills section (e.g. FollowupAnalysisPanel). */
  topContent?: ReactNode;
  /** TTS (Fase 4) — provisto por useAssistant. Sin estos props, los controles de voz de salida no se muestran. */
  muted?: boolean;
  onToggleMute?: () => void;
  isSpeaking?: boolean;
  onStopSpeaking?: () => void;
  /** Circuit breaker: se cortó de pedir audio tras una falla real — se muestra distinto de "muteado a propósito". */
  ttsBroken?: boolean;
  ttsErrorMessage?: string | null;
  onReactivateTts?: () => void;
  /** Visible cuando la última respuesta no trajo línea hablada usable — nunca silencio sin explicación. */
  voiceNote?: string | null;
  children: ReactNode;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function AssistantShell({
  title,
  pills,
  onSendMessage,
  isStreaming = false,
  inputPlaceholder,
  chipPlaceholder,
  inputDisabled = false,
  storageKey,
  footerOverride,
  topContent,
  muted,
  onToggleMute,
  isSpeaking = false,
  onStopSpeaking,
  ttsBroken = false,
  ttsErrorMessage,
  onReactivateTts,
  voiceNote,
  children,
}: Props) {
  const { t } = useTranslation();
  injectVoiceKeyframes();

  const [input, setInput]           = useState('');
  const [activePill, setActivePill] = useState<AssistantPillDef | null>(null);
  const inputRef                    = useRef<HTMLTextAreaElement>(null);

  const isDisabled = isStreaming || inputDisabled;

  // ── Voz de entrada (Fase 4) — push-to-talk ──────────────────────────────────
  const { data: groqStatus } = useSecretStatus('groq-api-key');
  const micEnabled = groqStatus?.configured === true;

  const handleTranscribed = useCallback((text: string) => {
    setInput(text);
    requestAnimationFrame(() => {
      inputRef.current?.focus();
      if (inputRef.current) {
        inputRef.current.style.height = 'auto';
        inputRef.current.style.height = `${inputRef.current.scrollHeight}px`;
      }
    });
  }, []);
  const stt = useSpeechToText(handleTranscribed);

  // Atajo de teclado: Ctrl+Espacio sostenido, mismo gesto que el botón.
  const spaceHeldRef = useRef(false);
  useEffect(() => {
    if (!micEnabled || isDisabled) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.ctrlKey && e.code === 'Space' && !spaceHeldRef.current) {
        e.preventDefault();
        spaceHeldRef.current = true;
        stt.startRecording();
      }
    }
    function onKeyUp(e: KeyboardEvent) {
      if (e.code === 'Space' && spaceHeldRef.current) {
        e.preventDefault();
        spaceHeldRef.current = false;
        stt.stopRecording();
      }
    }
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, [micEnabled, isDisabled, stt.startRecording, stt.stopRecording]);

  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(storageKey ?? 'evo-assistant-collapsed') === 'true';
    } catch {
      return false;
    }
  });

  function toggleCollapse() {
    const next = !collapsed;
    setCollapsed(next);
    try {
      localStorage.setItem(storageKey ?? 'evo-assistant-collapsed', String(next));
    } catch { /* storage unavailable — collapse works in-memory */ }
  }

  // ── Actions ───────────────────────────────────────────────────────────────

  function handlePillClick(pill: AssistantPillDef) {
    if (isStreaming) return;
    if (pill.requiresInput) {
      setActivePill(pill);
      setInput('');
      requestAnimationFrame(() => inputRef.current?.focus());
    } else {
      onSendMessage(pill.label);
    }
  }

  function handleChipCancel() {
    setActivePill(null);
    setInput('');
    requestAnimationFrame(() => inputRef.current?.focus());
  }

  function handleInputChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    setInput(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = `${e.target.scrollHeight}px`;
  }

  function handleSend() {
    const trimmed = input.trim();
    if (!trimmed || isDisabled) return;
    const msg = activePill ? `${activePill.label}: ${trimmed}` : trimmed;
    setInput('');
    setActivePill(null);
    onSendMessage(msg);
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
    }
  }

  // ── Collapsed strip ───────────────────────────────────────────────────────

  const collapsedStrip = (
    <div style={{
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      paddingTop: 'var(--evo-space-4)',
      paddingBottom: 'var(--evo-space-3)',
      background: 'var(--evo-bg-3)',
    }}>
      {/* Sparkle — click to expand */}
      <button
        onClick={toggleCollapse}
        aria-label="Expand assistant"
        style={{
          width: '36px',
          height: '36px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: 'var(--evo-cyan)',
          borderRadius: 'var(--evo-r-sm)',
          transition: 'opacity 120ms ease-out',
        }}
      >
        <Icon name="sparkle" size={20} />
      </button>

      {/* Spacer */}
      <div style={{ flex: 1 }} />

      {/* Chevron left — decorative, sparkle button above covers the action */}
      <button
        onClick={toggleCollapse}
        aria-hidden="true"
        tabIndex={-1}
        style={{
          width: '28px',
          height: '28px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: 'var(--evo-fg-3)',
          borderRadius: 'var(--evo-r-sm)',
          transition: 'color 120ms ease-out',
        }}
      >
        <Icon name="chevron-left" size={16} />
      </button>
    </div>
  );

  // ── Pinned pills section ──────────────────────────────────────────────────
  // Hidden when a pill chip is active (user is typing the instruction).

  const hasPills = pills && pills.length > 0;

  const pillsSection = hasPills && activePill == null ? (
    <div style={{
      padding: 'var(--evo-space-2) var(--evo-space-3)',
      borderBottom: '1px solid var(--evo-stroke)',
    }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 'var(--evo-space-2)',
      }}>
        {pills!.map(pill => (
          <button
            key={pill.toolName}
            disabled={isStreaming}
            onClick={() => handlePillClick(pill)}
            style={{
              textAlign: 'left',
              padding: '7px 10px',
              borderRadius: 'var(--evo-r)',
              border: '1px solid var(--evo-cyan)',
              background: 'transparent',
              color: isStreaming ? 'var(--evo-fg-4)' : 'var(--evo-cyan)',
              fontFamily: 'var(--evo-font)',
              fontSize: 'var(--evo-fs-caption)',
              cursor: isStreaming ? 'not-allowed' : 'pointer',
              lineHeight: 1.3,
              opacity: isStreaming ? 0.5 : 1,
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              transition: 'opacity 150ms ease-out',
            }}
          >
            {pill.requiresInput && <Icon name="pencil" size={10} />}
            {pill.label}
          </button>
        ))}
      </div>
    </div>
  ) : null;

  const pinnedTop = (topContent != null || pillsSection != null) ? (
    <>
      {topContent}
      {pillsSection}
    </>
  ) : null;

  // ── Collapse button for header ────────────────────────────────────────────

  const collapseBtn = (
    <button
      onClick={toggleCollapse}
      aria-label="Collapse assistant"
      style={{
        width: '24px',
        height: '24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        color: 'var(--evo-fg-3)',
        borderRadius: 'var(--evo-r-sm)',
        transition: 'color 120ms ease-out',
        flexShrink: 0,
      }}
    >
      <span style={{ display: 'inline-flex', transform: 'rotate(180deg)' }}>
        <Icon name="chevron-left" size={16} />
      </span>
    </button>
  );

  // ── Voz de salida — mute persistente + cortar lectura (Fase 4) ─────────────
  // Solo se muestran si el panel llamador pasó estos props (vienen de
  // useAssistant) — sin ellos, TTS no está wireado para ese panel.

  // Roto (falló de verdad, se cortó de pedir audio) es un estado DISTINTO de
  // muteado a propósito — mismo botón, pero rojo, y el click reactiva en vez
  // de mutear/desmutear. Ver tripTtsBreaker/reactivateTts en useAssistant.
  const muteBtnLabel = ttsBroken
    ? t('assistant.tts.reactivate')
    : (muted ? t('assistant.tts.unmute') : t('assistant.tts.mute'));

  const muteBtn = onToggleMute != null ? (
    <button
      onClick={ttsBroken ? onReactivateTts : onToggleMute}
      aria-label={muteBtnLabel}
      title={ttsBroken && ttsErrorMessage ? `${muteBtnLabel} — ${ttsErrorMessage}` : muteBtnLabel}
      style={{
        width: '24px',
        height: '24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        color: ttsBroken ? 'var(--evo-danger)' : (muted ? 'var(--evo-fg-4)' : 'var(--evo-fg-3)'),
        borderRadius: 'var(--evo-r-sm)',
        transition: 'color 120ms ease-out',
        flexShrink: 0,
      }}
    >
      <Icon name={(ttsBroken || muted) ? 'volume-mute' : 'volume'} size={15} />
    </button>
  ) : null;

  const stopSpeakingBtn = (isSpeaking && onStopSpeaking != null) ? (
    <button
      onClick={onStopSpeaking}
      aria-label={t('assistant.tts.stop')}
      title={t('assistant.tts.stop')}
      style={{
        width: '24px',
        height: '24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        color: 'var(--evo-cyan)',
        borderRadius: 'var(--evo-r-sm)',
        flexShrink: 0,
      }}
    >
      <Icon name="stop" size={13} />
    </button>
  ) : null;

  const headerControls = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-1)' }}>
      {stopSpeakingBtn}
      {muteBtn}
      {collapseBtn}
    </div>
  );

  // ── Standard footer — input + send + chip ─────────────────────────────────

  const standardFooter = (
    <div>
      {/* Chip — visible when a requiresInput pill is active */}
      {activePill != null && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--evo-space-2)',
          marginBottom: 'var(--evo-space-2)',
          padding: '5px 8px',
          background: 'var(--evo-bg-2)',
          border: '1px solid var(--evo-cyan)',
          borderRadius: 'var(--evo-r-sm)',
        }}>
          <span style={{ color: 'var(--evo-cyan)', flexShrink: 0, display: 'inline-flex' }}>
            <Icon name="arrow-right" size={10} />
          </span>
          <span style={{
            flex: 1,
            fontSize: 'var(--evo-fs-caption)',
            fontFamily: 'var(--evo-font)',
            color: 'var(--evo-cyan)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {activePill.label}
          </span>
          <button
            onClick={handleChipCancel}
            style={{
              width: '16px',
              height: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--evo-fg-3)',
              padding: 0,
              borderRadius: 'var(--evo-r-sm)',
            }}
          >
            <Icon name="close" size={10} />
          </button>
        </div>
      )}

      {/* Input row */}
      <div style={{ display: 'flex', gap: 'var(--evo-space-2)', alignItems: 'flex-end' }}>
        <textarea
          ref={inputRef}
          value={input}
          onChange={handleInputChange}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
            if (e.key === 'Escape' && activePill != null) { e.preventDefault(); handleChipCancel(); }
          }}
          disabled={isDisabled}
          rows={1}
          aria-label={
            activePill != null
              ? (chipPlaceholder ?? 'Describe the instruction…')
              : (inputPlaceholder ?? 'Ask Claude…')
          }
          placeholder={
            activePill != null
              ? (chipPlaceholder ?? 'Describe the instruction…')
              : (inputPlaceholder ?? 'Ask Claude…')
          }
          style={{
            flex: 1,
            minHeight: '32px',
            maxHeight: '200px',
            padding: '7px 10px',
            background: 'var(--evo-bg)',
            border: `1px solid ${activePill != null ? 'var(--evo-cyan)' : 'var(--evo-stroke)'}`,
            borderRadius: 'var(--evo-r)',
            color: 'var(--evo-fg-1)',
            fontFamily: 'var(--evo-font)',
            fontSize: 'var(--evo-fs-label)',
            opacity: isDisabled ? 0.55 : 1,
            outline: 'none',
            resize: 'none',
            overflowY: 'auto',
            lineHeight: '1.45',
            transition: 'border-color 120ms ease-out',
          }}
        />
        {micEnabled && (
          <button
            disabled={isDisabled || stt.isTranscribing}
            onMouseDown={stt.startRecording}
            onMouseUp={stt.stopRecording}
            onMouseLeave={() => { if (stt.isRecording) stt.stopRecording(); }}
            onTouchStart={e => { e.preventDefault(); stt.startRecording(); }}
            onTouchEnd={e => { e.preventDefault(); stt.stopRecording(); }}
            aria-label={stt.isRecording ? t('assistant.mic.recording') : t('assistant.mic.label')}
            title={t('assistant.mic.hint')}
            style={{
              width: '32px',
              height: '32px',
              borderRadius: 'var(--evo-r)',
              border: `1px solid ${stt.isRecording ? 'var(--evo-danger)' : 'var(--evo-stroke)'}`,
              background: stt.isRecording ? 'var(--evo-danger)' : 'transparent',
              color: stt.isRecording ? '#fff' : 'var(--evo-fg-3)',
              cursor: (isDisabled || stt.isTranscribing) ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              opacity: (isDisabled || stt.isTranscribing) ? 0.5 : 1,
              animation: stt.isRecording ? 'evo-mic-pulse 1s ease-in-out infinite' : 'none',
              transition: 'background 120ms ease-out, border-color 120ms ease-out',
            }}
          >
            <Icon name="mic" size={14} />
          </button>
        )}
        <button
          disabled={isDisabled || !input.trim()}
          onClick={handleSend}
          style={{
            width: '32px',
            height: '32px',
            borderRadius: 'var(--evo-r)',
            border: 'none',
            background: (!isDisabled && input.trim()) ? 'var(--evo-cyan)' : 'var(--evo-stroke)',
            color: (!isDisabled && input.trim()) ? 'var(--evo-fg-on-accent)' : 'var(--evo-fg-3)',
            cursor: (!isDisabled && input.trim()) ? 'pointer' : 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            transition: 'background 120ms ease-out',
          }}
        >
          <Icon name="send" size={14} />
        </button>
      </div>

      {micEnabled && (stt.isRecording || stt.isTranscribing) && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '5px', marginTop: 'var(--evo-space-1)',
          fontSize: 'var(--evo-fs-caption)', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-3)',
        }}>
          {stt.isRecording ? t('assistant.mic.recording') : t('assistant.mic.transcribing')}
        </div>
      )}

      {stt.error != null && (
        <div style={{
          marginTop: 'var(--evo-space-1)', fontSize: 'var(--evo-fs-caption)', fontFamily: 'var(--evo-font)',
          color: 'var(--evo-danger)',
        }}>
          {t('assistant.mic.errorLabel')}: {stt.error}
        </div>
      )}

      {ttsBroken && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '5px', marginTop: 'var(--evo-space-1)',
          fontSize: 'var(--evo-fs-caption)', fontFamily: 'var(--evo-font)', color: 'var(--evo-danger)',
        }}>
          {t('assistant.tts.brokenLabel')}{ttsErrorMessage ? `: ${ttsErrorMessage}` : ''} — {t('assistant.tts.brokenHint')}
        </div>
      )}

      {!ttsBroken && voiceNote != null && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '5px', marginTop: 'var(--evo-space-1)',
          fontSize: 'var(--evo-fs-caption)', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-4)',
        }}>
          {voiceNote}
        </div>
      )}
    </div>
  );

  return (
    <div style={{
      width: collapsed ? 'var(--evo-assistant-w-collapsed)' : 'var(--evo-claude-panel-w)',
      flexShrink: 0,
      borderLeft: '1px solid var(--evo-stroke)',
      height: '100%',
      overflow: 'hidden',
      transition: 'width var(--evo-motion-collapse)',
    }}>
      {collapsed ? collapsedStrip : (
        <ClaudeAssistantPanel
          title={title}
          headerRight={headerControls}
          pinnedTop={pinnedTop}
          footer={footerOverride ?? standardFooter}
        >
          {children}
        </ClaudeAssistantPanel>
      )}
    </div>
  );
}
